
import type { StatsResponse } from '@empathybroker/tsclient-stats/dist/models/stats-response'
import type { KpisLegendItem } from '../../../composables/use-distribution-chart'

import type { KpisOptionComposable } from '../../../composables/use-kpis'

import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum'
import { computed, defineComponent, getCurrentInstance, ref, watch } from 'vue'

import { useDistributionChart } from '../../../composables/use-distribution-chart'
import { mapGetters } from '../../../composables/utils'
import { KpiEnum, OriginsEnum } from '../../../enums'

import { ANALYTICS_CONSTS } from '../../../store/analytics/analytics.consts'

import KpisLegend from '../../kpis-legend.component.vue'

interface SemanticsDistributionChartData {
  date: Date
  queryCountSemantics: number
  clickCount: number
  add2cartCount: number
  noResultsCount?: number
}

export default defineComponent({
  name: 'SemanticsDistributionChart',
  components: {
    KpisLegend,
  },
  props: {
    selectedId: {
      type: String,
      default: undefined,
    },
    selectedTab: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const baseClass = 'semantics-distribution-chart'

    const ctx = getCurrentInstance()
    if (!ctx) {
      throw new Error("'ctx' is not defined")
    }

    const messages = ctx.proxy.$epb.messages.insights.semantics.timelineGraph

    const { analyticsData } = mapGetters(ctx.proxy.$store, ANALYTICS_CONSTS.moduleName, {
      analyticsData: ANALYTICS_CONSTS.getters.data,
    })

    const semanticsNoResultsData = ref()
    const semanticsLowResultsData = ref()

    const loading = ref(false)

    const kpiOptions = computed((): KpisOptionComposable[] => {
      const options = [
        { id: KpiEnum.SEMANTICS_QUERIES, label: messages.semanticsQueries },
        { id: KpiEnum.CLICKS, label: messages.clicks },
        { id: KpiEnum.ADD2CART, label: messages.add2carts },
      ]
      if (props.selectedTab !== OriginsEnum.LOW_RESULTS) {
        options.splice(1, 0, { id: KpiEnum.NO_RESULTS, label: messages.noResults })
      }
      return options
    })

    const kpisItems = computed((): KpisLegendItem[] => {
      if (props.selectedTab === OriginsEnum.LOW_RESULTS) {
        return kpiOptions.value.map((option: KpisOptionComposable) => ({
          ...option,
          unfilled: true,
        }))
      }
      return kpiOptions.value.map((option: KpisOptionComposable) => ({
        ...option,
        unfilled: true,
      }))
    })

    const getKpiValueFn = (kpi: KpiEnum): ((data: SemanticsDistributionChartData) => number) => {
      return {
        [KpiEnum.SEMANTICS_QUERIES]: (data: SemanticsDistributionChartData) =>
          data.queryCountSemantics,
        [KpiEnum.NO_RESULTS]: (data: SemanticsDistributionChartData) => data.noResultsCount ?? 0,
        [KpiEnum.CLICKS]: (data: SemanticsDistributionChartData) => data.clickCount,
        [KpiEnum.ADD2CART]: (data: SemanticsDistributionChartData) => data.add2cartCount,
      }[kpi]
    }

    const {
      timezone,
      selectedKpis,
      chartData: semanticsDistributionChartData,
      isDailyData,
      context,
      fetchStatsReport,
      loadDistData,
    } = useDistributionChart<SemanticsDistributionChartData>(
      kpiOptions.value,
      kpisItems.value,
      getKpiValueFn,
    )

    const fetchDistributionChartData = async function (originId: string): Promise<StatsResponse> {
      const response: StatsResponse = await fetchStatsReport({
        id: isDailyData.value
          ? StatsReportsIdsEnum.HOURLY_ORIGINS
          : StatsReportsIdsEnum.DAILY_ORIGINS,
        params: {
          origins: [originId],
        },
      })

      return response.data.dist
    }

    const loadData = async function (): Promise<void> {
      loading.value = true
      try {
        semanticsNoResultsData.value = await fetchDistributionChartData(
          'semantics:no_results,overview:no_results,ai_carousel:no_results',
        )
        semanticsLowResultsData.value = await fetchDistributionChartData(
          'semantics:low_results,overview:low_results,ai_carousel:low_results',
        )
      } catch (error) {
        ctx.proxy.$emit('showError', ctx.proxy.$epb.utils.decodeErrorMessage(error))
      } finally {
        loading.value = false
      }
    }

    const updateDistributionData = (): void => {
      let data = []
      if (
        props.selectedTab === OriginsEnum.OVERALL &&
        semanticsNoResultsData.value &&
        semanticsLowResultsData.value
      ) {
        data = semanticsNoResultsData.value.map(
          (noResultsData: SemanticsDistributionChartData, index: number) => {
            const lowResultsData = semanticsLowResultsData.value[index]
            return {
              date: noResultsData.date,
              queryCountSemantics:
                noResultsData.queryCountSemantics + lowResultsData.queryCountSemantics,
              clickCount: noResultsData.clickCount + lowResultsData.clickCount,
              add2cartCount: noResultsData.add2cartCount + lowResultsData.add2cartCount,
              noResultsCount:
                analyticsData.value.overall.dist.currentPeriod.data?.distributionData[index]
                  ?.noResultsCount ?? 0,
            }
          },
        )
      } else if (
        (props.selectedTab === OriginsEnum.OVERALL && semanticsNoResultsData.value) ||
        props.selectedTab === OriginsEnum.NO_RESULTS
      ) {
        data = semanticsNoResultsData.value.map(
          ({ date, queryCountSemantics, clickCount, add2cartCount }, index) => ({
            date,
            queryCountSemantics,
            clickCount,
            add2cartCount,
            noResultsCount:
              analyticsData.value.overall.dist.currentPeriod.data?.distributionData[index]
                ?.noResultsCount ?? 0,
          }),
        )
      } else if (
        (props.selectedTab === OriginsEnum.OVERALL && semanticsLowResultsData.value) ||
        props.selectedTab === OriginsEnum.LOW_RESULTS
      ) {
        data = semanticsLowResultsData.value
      }
      semanticsDistributionChartData.value = data
      loading.value = false
    }

    watch(
      () => context.value.filters,
      async () => {
        await loadDistData()
        await loadData()
      },
      { immediate: true },
    )

    watch(
      () => [props.selectedTab, analyticsData.value.overall.dist],
      () => {
        updateDistributionData()
      },
    )

    return {
      baseClass,
      isDailyData,
      kpisItems,
      loading,
      selectedKpis,
      timezone,
    }
  },
})
